import React, { useState } from "react";
import { Container, Row, Col, Table, Form, Button, Card } from "react-bootstrap";
import { FaPrint, FaDownload, FaSearch, FaChartLine, FaFileInvoice, FaMoneyBillWave, FaFilter } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";

const SalesWiseLedger = () => {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    transactionType: "all",
    name: ""
  });
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");

  // Sample sales data
  const [salesData, setSalesData] = useState([
    {
      id: 1,
      date: "2025-11-21",
      invoiceNumber: "INV-S-001",
      customerName: "SSS Enterprises",
      customerGSTIN: "07AABCU9603R1ZM",
      taxableValue: 15000.00,
      totalGST: 2700.00,
      totalAmount: 17700.00,
      paymentStatus: "Paid",
      dueDate: "2025-12-21",
      balanceAmount: 0.00
    },
    {
      id: 2,
      date: "2025-11-20",
      invoiceNumber: "INV-S-002",
      customerName: "ABC Traders",
      customerGSTIN: "27BXYPS1205Q2Z4",
      taxableValue: 8500.00,
      totalGST: 1530.00,
      totalAmount: 10030.00,
      paymentStatus: "Pending",
      dueDate: "2025-12-20",
      balanceAmount: 10030.00
    },
    {
      id: 3,
      date: "2025-11-19",
      invoiceNumber: "INV-S-003",
      customerName: "Global Exports",
      customerGSTIN: "09PQRSM7896L3T8",
      taxableValue: 25000.00,
      totalGST: 4500.00,
      totalAmount: 29500.00,
      paymentStatus: "Paid",
      dueDate: "2025-12-19",
      balanceAmount: 0.00
    },
    {
      id: 4,
      date: "2025-11-18",
      invoiceNumber: "INV-S-004",
      customerName: "Metro Distributors",
      customerGSTIN: "24ABCDE1234F5G6",
      taxableValue: 12000.00,
      totalGST: 2160.00,
      totalAmount: 14160.00,
      paymentStatus: "Partial",
      dueDate: "2025-12-18",
      balanceAmount: 5000.00
    },
    {
      id: 5,
      date: "2025-11-17",
      invoiceNumber: "INV-S-005",
      customerName: "SSS Enterprises",
      customerGSTIN: "07AABCU9603R1ZM",
      taxableValue: 9000.00,
      totalGST: 1620.00,
      totalAmount: 10620.00,
      paymentStatus: "Paid",
      dueDate: "2025-12-17",
      balanceAmount: 0.00
    },
    {
      id: 6,
      date: "2025-11-16",
      invoiceNumber: "INV-S-006",
      customerName: "National Retailers",
      customerGSTIN: "33MMTTS5566K9L0",
      taxableValue: 18000.00,
      totalGST: 3240.00,
      totalAmount: 21240.00,
      paymentStatus: "Pending",
      dueDate: "2025-12-16",
      balanceAmount: 21240.00
    }
  ]);

  // Get unique customers for filter
  const customers = [...new Set(salesData.map(sale => sale.customerName))];

  // Filter sales data based on date range, customer, and payment status
  const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};


const filteredSales = salesData.filter((sale) => {
  // 🔍 Search
  const matchesSearch =
    !search ||
    sale.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    sale.invoiceNumber?.toLowerCase().includes(search.toLowerCase());

  // 📅 Date
  const saleDate = sale.date ? parseDDMMYYYY(sale.date) : null;

  const from = filters.startDate
    ? parseYYYYMMDD(filters.startDate)
    : null;

  const to = filters.endDate
    ? parseYYYYMMDD(filters.endDate)
    : null;

  if (to) to.setHours(23, 59, 59, 999);

  const matchesDate =
    !saleDate ||
    ((!from || saleDate >= from) &&
     (!to || saleDate <= to));

  return matchesSearch && matchesDate;
});


  // Calculate totals
  const calculateTotals = () => {
    return filteredSales.reduce((totals, sale) => {
      return {
        totalTaxableValue: totals.totalTaxableValue + sale.taxableValue,
        totalGST: totals.totalGST + sale.totalGST,
        totalAmount: totals.totalAmount + sale.totalAmount,
        totalBalance: totals.totalBalance + sale.balanceAmount
      };
    }, {
      totalTaxableValue: 0,
      totalGST: 0,
      totalAmount: 0,
      totalBalance: 0
    });
  };

  const totals = calculateTotals();

  // NEW: Custom payment status badge using CSS classes
  const getPaymentStatusClass = (status) => {
    const statusClassMap = {
      Paid: 'status-badge status-completed',
      Pending: 'status-badge status-pending',
      Partial: 'status-badge status-progress',
      Overdue: 'status-badge status-rejected'
    };

    return statusClassMap[status] || 'status-badge status-default';
  };

  // Get due status (keeping Bootstrap badges for these as they are supplementary)
  const getDueStatus = (dueDate, paymentStatus) => {
    if (paymentStatus === 'Paid') return null;

    const today = new Date();
    const due = new Date(dueDate);

    if (due < today) {
      return <span className="status-badge status-rejected ms-1">Overdue</span>;
    }

    const daysUntilDue = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (daysUntilDue <= 7) {
      return <span className="status-badge status-pending ms-1">Due Soon</span>;
    }

    return null;
  };

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentSales,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredSales, 10);

  const handlePrint = () => {
    window.print();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExport = () => {
    const headers = ['Date', 'Invoice No', 'Customer Name', 'Customer GSTIN', 'Taxable Value', 'Total GST', 'Total Amount', 'Payment Status', 'Due Date', 'Balance Amount'];
    const csvData = filteredSales.map(sale => [
      sale.date,
      sale.invoiceNumber,
      sale.customerName,
      sale.customerGSTIN,
      sale.taxableValue,
      sale.totalGST,
      sale.totalAmount,
      sale.paymentStatus,
      sale.dueDate,
      sale.balanceAmount
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales-ledger-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setCustomerFilter("");
    setPaymentStatusFilter("");
  };

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Sales Wise Ledger</h2>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-primary text-white">
            <Card.Body>
              <h4 className="mb-1">{filteredSales.length}</h4>
              <small>Total Invoices</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-success text-white">
            <Card.Body>
              <h4 className="mb-1">₹{totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <small>Total Sales</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-info text-white">
            <Card.Body>
              <h4 className="mb-1">₹{totals.totalGST.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <small>Total GST</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-warning text-dark">
            <Card.Body>
              <h4 className="mb-1">₹{totals.totalTaxableValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <small>Taxable Value</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-danger text-white">
            <Card.Body>
              <h4 className="mb-1">₹{totals.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <small>Pending Amount</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-secondary text-white">
            <Card.Body>
              <h4 className="mb-1">
                {filteredSales.filter(s => s.paymentStatus === 'Pending').length}
              </h4>
              <small>Pending Invoices</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      <div className="filter-section mb-3 p-3 bg-light rounded shadow-sm">
        <Row className="align-items-center mb-2">
          <Col>
            <h5 className="mb-0">
              <FaFilter className="me-2" />
              Filters
            </h5>
          </Col>
        </Row>

        <Row className="g-2">
          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} lg={4} className="mb-2 mb-md-0">
            <div className="position-relative">
              <Form.Label>Search</Form.Label>
              <Form.Control
                placeholder="Search by Supplier Name,Purchase No"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch("");
                }}
              />
              {/* <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" /> */}
            </div>
          </Col>
<Col xs={12} md={2} lg={2} className="mb-2 mb-md-0">
            <div className="position-relative">
              <Form.Label> Print</Form.Label>
              <Button className="btn-submit" onClick={handlePrint}>
                <FaPrint size={20} />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Sales Ledger Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table sales-ledger-table">
            <thead className="table-dark">
              <tr>
                <th>Date</th>
                <th>Invoice No</th>
                <th>Customer Name</th>
                <th>Customer GSTIN</th>
                <th className="text-end">Total Amount</th>
                <th>Status</th>
                <th>Due Date</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {currentSales.map((sale, index) => (
                <tr key={sale.id}>
                  <td className="text-center">{sale.date}</td>
                  <td className="invoice-no-column">
                    <strong>{sale.invoiceNumber}</strong>
                  </td>
                  <td>{sale.customerName}</td>
                  <td>
                    <small>{sale.customerGSTIN}</small>
                  </td>
                  <td className="text-end fw-bold">₹{sale.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <span className={getPaymentStatusClass(sale.paymentStatus)}>
                      {sale.paymentStatus}
                    </span>
                    {getDueStatus(sale.dueDate, sale.paymentStatus)}
                  </td>
                  <td className="text-center">{sale.dueDate}</td>
                  <td className="text-end">
                    {sale.balanceAmount > 0 ? (
                      <span className="text-danger fw-bold">
                        ₹{sale.balanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <span className="text-success">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {currentSales.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <div className="text-muted">
                      <FaChartLine size={48} className="mb-3" />
                      <p>No sales transactions found</p>
                      <small>Try adjusting your filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {/* Summary Footer Row */}
            {currentSales.length > 0 && (
              <tfoot className="table-warning">
                <tr>
                  <td colSpan="4" className="text-end fw-bold">Totals:</td>
                  <td className="text-end fw-bold">₹{totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td colSpan="2"></td>
                  <td className="text-end fw-bold text-danger">₹{totals.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Section */}
      <Row className="mt-4">
        <Col xs={12} md={6}>
          <Card className="border-0 bg-light">
            <Card.Header className="bg-dark text-white">
              <h6 className="mb-0">Payment Summary</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={6}>
                  <strong>Paid Invoices:</strong>
                </Col>
                <Col xs={6} className="text-end">
                  {filteredSales.filter(s => s.paymentStatus === 'Paid').length}
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <strong>Pending Invoices:</strong>
                </Col>
                <Col xs={6} className="text-end">
                  {filteredSales.filter(s => s.paymentStatus === 'Pending').length}
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <strong>Partial Payments:</strong>
                </Col>
                <Col xs={6} className="text-end">
                  {filteredSales.filter(s => s.paymentStatus === 'Partial').length}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col xs={6}>
                  <strong>Collection Rate:</strong>
                </Col>
                <Col xs={6} className="text-end fw-bold text-primary">
                  {filteredSales.length > 0 ?
                    (((totals.totalAmount - totals.totalBalance) / totals.totalAmount) * 100).toFixed(1) + '%'
                    : '0%'
                  }
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="border-0 bg-light">
            <Card.Header className="bg-dark text-white">
              <h6 className="mb-0">Amount Summary</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={6}>
                  <strong>Total Billed:</strong>
                </Col>
                <Col xs={6} className="text-end">
                  ₹{totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <strong>Total Received:</strong>
                </Col>
                <Col xs={6} className="text-end text-success">
                  ₹{(totals.totalAmount - totals.totalBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <strong>Total Pending:</strong>
                </Col>
                <Col xs={6} className="text-end text-danger">
                  ₹{totals.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SalesWiseLedger;